import {useEffect, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {PhoneIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {phoneNumberFormat} from "@pg-mono/string-utils";
import {OfferStatusStatus} from "@pg-rp/common-types/src/types/OfferStatusStatus";

import {useAppSelector} from "../../../store/hooks";
import {goodbyeModalContentInitialized} from "../../../tracking/algolytics_hits/goodbye_modal_content_initialized_hit";
import {gtmRotatingPhoneNumbersCall, gtmRotatingPhoneNumbersReveal} from "../../../tracking/google_tag_manager/gtm_rotating_phone_numbers";
import {hotlineTelNumber} from "../../../types/HotlineTelNumber";
import {useApplicationPhoneClipNumber} from "../../hooks/use_application_phone_clip_number";
import {useLeadData} from "../../hooks/use_lead_data";
import {useLeadRequiredParams} from "../../hooks/use_lead_required_params";
import {LeadButtonTerms} from "../LeadButtonTerms";
import {LeadRodoTerms} from "../LeadRodoTerms";
import {ApplicationGoodbyeContentVendorDisplay} from "./ApplicationGoodbyeContentVendorDisplay";

export interface IApplicationGoodbyeContentProps {
    className?: string;
    closeModal: () => void;
}

const leadButtonText = "POKAŻ NUMER";

export const ApplicationGoodbyeContent = (props: IApplicationGoodbyeContentProps) => {
    const [showGoodbyeContent, setShowGoodbyeContent] = useState(true);
    const [showPhoneNumberContent, setShowPhoneNumberContent] = useState(false);
    const {vendorId, offerId, source, sourceSection} = useLeadRequiredParams();
    const data = useLeadData({vendorId, offerId: offerId || undefined});
    const offer = useAppSelector((state) => state.offerDetail.offer);

    useEffect(() => {
        checkForExceptionsInClosingModal();
    }, []);

    const checkForExceptionsInClosingModal = () => {
        const vendor = data && data.vendor;
        const show_form_placeholder = vendor && vendor.detail && vendor.detail.configuration && vendor.detail.configuration.show_form_placeholder;
        const phoneNumber = getPhoneNumber();

        if (show_form_placeholder || !phoneNumber) {
            setShowGoodbyeContent(false);
            return props.closeModal();
        }

        goodbyeModalContentInitialized(source, sourceSection, "third_scenario");
    };

    const getPhoneNumber = (): string | undefined => {
        const offer = data && data.offer;
        const vendor = data && data.vendor;
        const phone_clip = offer && offer.detail && offer.detail.configuration && offer.detail.configuration.phone_clip;
        const is_phone_clip_enabled = offer && offer.detail && offer.detail.is_phone_clip_enabled;
        const salesOffice = vendor && vendor.detail && vendor.detail.offices;

        if (phone_clip && is_phone_clip_enabled) {
            return phoneNumberFormat(phone_clip);
        }

        if (offer && offer.detail && offer.detail.configuration && offer.detail.configuration.phone_number) {
            return phoneNumberFormat(offer.detail.configuration.phone_number);
        }

        if (salesOffice && salesOffice.length) {
            const salesOfficeNumber = salesOffice[0].phone || salesOffice[0].mobile;
            return phoneNumberFormat(salesOfficeNumber as string);
        }
    };

    const {phoneNumber: clipPhone, fetchClipPhoneNumber, isFetching, isClipPhoneBlocked} = useApplicationPhoneClipNumber(getPhoneNumber());
    const phoneNumber = isClipPhoneBlocked ? data?.vendor?.detail?.offices[0].phone || data?.vendor?.detail?.offices[0].mobile : clipPhone;
    const phoneNumberToShow = offer?.status?.status === OfferStatusStatus.STATUS_STATUS_TO_ACQUIRE ? hotlineTelNumber : phoneNumber;

    const onShowPhoneNumberButtonClick = async () => {
        setShowPhoneNumberContent(true);
        gtmRotatingPhoneNumbersReveal("modal");
        await fetchClipPhoneNumber(data?.property?.detail?.id);
    };

    if (!showGoodbyeContent) {
        return null;
    }

    const vendorDetail = data?.vendor?.detail || null;
    const offerDetail = data?.offer?.detail || null;

    return (
        <div className={cx(goodbyeHolderStyle, props.className)}>
            <ApplicationGoodbyeContentVendorDisplay offer={offerDetail} onCloseModal={props.closeModal} vendor={vendorDetail} className={vendorDisplayStyle} />
            <Text className={headingStyle} align="center">
                Może chcesz skontaktować się z deweloperem telefonicznie?
            </Text>

            {!showPhoneNumberContent ? (
                <div className={buttonWrapperStyle}>
                    <Button onClick={onShowPhoneNumberButtonClick} type="button" variant="outlined_secondary" className={buttonStyle}>
                        <PhoneIcon size="2.4" wrapperSize="4" className={css({mr: 2})} />
                        {leadButtonText}
                    </Button>
                </div>
            ) : isFetching ? (
                <Loader size="sm" />
            ) : phoneNumberToShow ? (
                <Button
                    variant="filled_primary"
                    iconLeft={PhoneIcon}
                    href={`tel:+${phoneNumberToShow}`}
                    onClick={() => gtmRotatingPhoneNumbersCall("modal")}
                    className={callBtnStyle}
                >
                    {phoneNumberFormat(phoneNumberToShow) || "BRAK DANYCH KONTAKTOWYCH"}
                </Button>
            ) : null}

            <div className={rodoStyle}>
                <LeadButtonTerms buttonText={leadButtonText} />
                <LeadRodoTerms />
            </div>
        </div>
    );
};

const goodbyeHolderStyle = css({
    py: 6,
    px: 2,
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    md: {
        padding: 5
    }
});

const vendorDisplayStyle = css({
    marginBottom: "128px",
    display: "flex",
    width: "100%",
    justifyContent: "center",
    md: {
        marginBottom: "96px",
        textAlign: "center"
    }
});

const headingStyle = css({
    mb: 4,
    color: "var(--colors-gray-800)",
    maxWidth: "504px"
});

const buttonWrapperStyle = css({
    mb: 3,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const buttonStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const callBtnStyle = css({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textDecoration: "none",
    md: {
        width: "50rem"
    }
});

const rodoStyle = css({
    marginTop: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    paddingBottom: 2,
    md: {
        width: "60%"
    }
});
import {useEffect, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {PhoneIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {phoneNumberFormat} from "@pg-mono/string-utils";
import {OfferStatusStatus} from "@pg-rp/common-types/src/types/OfferStatusStatus";

import {useAppSelector} from "../../../store/hooks";
import {goodbyeModalContentInitialized} from "../../../tracking/algolytics_hits/goodbye_modal_content_initialized_hit";
import {gtmRotatingPhoneNumbersCall, gtmRotatingPhoneNumbersReveal} from "../../../tracking/google_tag_manager/gtm_rotating_phone_numbers";
import {hotlineTelNumber} from "../../../types/HotlineTelNumber";
import {useApplicationPhoneClipNumber} from "../../hooks/use_application_phone_clip_number";
import {useLeadData} from "../../hooks/use_lead_data";
import {useLeadRequiredParams} from "../../hooks/use_lead_required_params";
import {LeadButtonTerms} from "../LeadButtonTerms";
import {LeadRodoTerms} from "../LeadRodoTerms";
import {ApplicationGoodbyeContentVendorDisplay} from "./ApplicationGoodbyeContentVendorDisplay";

export interface IApplicationGoodbyeContentProps {
    className?: string;
    closeModal: () => void;
}

const leadButtonText = "POKAŻ NUMER";

export const ApplicationGoodbyeContent = (props: IApplicationGoodbyeContentProps) => {
    const [showGoodbyeContent, setShowGoodbyeContent] = useState(true);
    const [showPhoneNumberContent, setShowPhoneNumberContent] = useState(false);
    const {vendorId, offerId, source, sourceSection} = useLeadRequiredParams();
    const data = useLeadData({vendorId, offerId: offerId || undefined});
    const offer = useAppSelector((state) => state.offerDetail.offer);

    useEffect(() => {
        checkForExceptionsInClosingModal();
    }, []);

    const checkForExceptionsInClosingModal = () => {
        const vendor = data && data.vendor;
        const show_form_placeholder = vendor && vendor.detail && vendor.detail.configuration && vendor.detail.configuration.show_form_placeholder;
        const phoneNumber = getPhoneNumber();

        if (show_form_placeholder || !phoneNumber) {
            setShowGoodbyeContent(false);
            return props.closeModal();
        }

        goodbyeModalContentInitialized(source, sourceSection, "third_scenario");
    };

    const getPhoneNumber = (): string | undefined => {
        const offer = data && data.offer;
        const vendor = data && data.vendor;
        const phone_clip = offer && offer.detail && offer.detail.configuration && offer.detail.configuration.phone_clip;
        const is_phone_clip_enabled = offer && offer.detail && offer.detail.is_phone_clip_enabled;
        const salesOffice = vendor && vendor.detail && vendor.detail.offices;

        if (phone_clip && is_phone_clip_enabled) {
            return phoneNumberFormat(phone_clip);
        }

        if (offer && offer.detail && offer.detail.configuration && offer.detail.configuration.phone_number) {
            return phoneNumberFormat(offer.detail.configuration.phone_number);
        }

        if (salesOffice && salesOffice.length) {
            const salesOfficeNumber = salesOffice[0].phone || salesOffice[0].mobile;
            return phoneNumberFormat(salesOfficeNumber as string);
        }
    };

    const {phoneNumber: clipPhone, fetchClipPhoneNumber, isFetching, isClipPhoneBlocked} = useApplicationPhoneClipNumber(getPhoneNumber());
    const phoneNumber = isClipPhoneBlocked ? data?.vendor?.detail?.offices[0].phone || data?.vendor?.detail?.offices[0].mobile : clipPhone;
    const phoneNumberToShow = offer?.status?.status === OfferStatusStatus.STATUS_STATUS_TO_ACQUIRE ? hotlineTelNumber : phoneNumber;

    const onShowPhoneNumberButtonClick = async () => {
        setShowPhoneNumberContent(true);
        gtmRotatingPhoneNumbersReveal("modal");
        await fetchClipPhoneNumber(data?.property?.detail?.id);
    };

    if (!showGoodbyeContent) {
        return null;
    }

    const vendorDetail = data?.vendor?.detail || null;
    const offerDetail = data?.offer?.detail || null;

    return (
        <div className={cx(goodbyeHolderStyle, props.className)}>
            <ApplicationGoodbyeContentVendorDisplay offer={offerDetail} onCloseModal={props.closeModal} vendor={vendorDetail} className={vendorDisplayStyle} />
            <Text className={headingStyle} align="center">
                Może chcesz skontaktować się z deweloperem telefonicznie?
            </Text>

            {!showPhoneNumberContent ? (
                <div className={buttonWrapperStyle}>
                    <Button onClick={onShowPhoneNumberButtonClick} type="button" variant="outlined_secondary" className={buttonStyle}>
                        <PhoneIcon size="2.4" wrapperSize="4" className={css({mr: 2})} />
                        {leadButtonText}
                    </Button>
                </div>
            ) : isFetching ? (
                <Loader size="sm" />
            ) : phoneNumberToShow ? (
                <Button
                    variant="filled_primary"
                    iconLeft={PhoneIcon}
                    href={`tel:+${phoneNumberToShow}`}
                    onClick={() => gtmRotatingPhoneNumbersCall("modal")}
                    className={callBtnStyle}
                >
                    {phoneNumberFormat(phoneNumberToShow) || "BRAK DANYCH KONTAKTOWYCH"}
                </Button>
            ) : null}

            <div className={rodoStyle}>
                <LeadButtonTerms buttonText={leadButtonText} />
                <LeadRodoTerms />
            </div>
        </div>
    );
};

const goodbyeHolderStyle = css({
    py: 6,
    px: 2,
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    md: {
        padding: 5
    }
});

const vendorDisplayStyle = css({
    marginBottom: "128px",
    display: "flex",
    width: "100%",
    justifyContent: "center",
    md: {
        marginBottom: "96px",
        textAlign: "center"
    }
});

const headingStyle = css({
    mb: 4,
    color: "var(--colors-gray-800)",
    maxWidth: "504px"
});

const buttonWrapperStyle = css({
    mb: 3,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const buttonStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const callBtnStyle = css({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textDecoration: "none",
    md: {
        width: "50rem"
    }
});

const rodoStyle = css({
    marginTop: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    paddingBottom: 2,
    md: {
        width: "60%"
    }
});
